import React from 'react';
import { AppAlert, AppBadge, AppButton, AppInput, AppModal } from '../../components/ui';
import { recipientStatusLabel } from './itiMaintenanceMails.helpers';

export function ItiMaintenanceConfirmModal({
  open,
  groups,
  overrides,
  testMode,
  testEmail,
  replyTo,
  subject,
  sending,
  progress,
  onClose,
  onConfirm,
  onAbort,
}) {
  const sendable = groups.filter((group) => group.email);
  const skipped = groups.filter((group) => !group.email);
  const total = groups.length;
  const current = progress?.current ?? 0;

  return (
    <AppModal
      show={open}
      title={sending ? 'Envoi en cours' : 'Confirmer l’envoi'}
      onClose={onClose}
      closeOnOverlay={!sending}
      closeDisabled={sending}
      size="lg"
      centered
      scrollable
      className="iti-maint-modal"
      footer={sending ? (
        <AppButton variant="outline-danger" onClick={onAbort}>
          Interrompre
        </AppButton>
      ) : (
        <>
          <AppButton variant="secondary" onClick={onClose}>Annuler</AppButton>
          <AppButton onClick={onConfirm} disabled={sendable.length === 0}>
            {testMode ? `Envoyer le test (${sendable.length})` : `Envoyer (${sendable.length})`}
          </AppButton>
        </>
      )}
    >
      <p className="mb-2">
        {testMode
          ? `${sendable.length} mail(s) de test vers ${testEmail}.`
          : `${sendable.length} mail(s) individuel(s) depuis no-reply@iti-conseil.com.`}
        {replyTo ? ` Reply-To : ${replyTo}.` : ''}
      </p>
      <div className="fw-semibold mb-2">{subject || 'Sans objet'}</div>
      {skipped.length > 0 && (
        <AppAlert variant="warning" className="py-2">
          {skipped.length} destinataire(s) sans e-mail seront ignorés.
        </AppAlert>
      )}
      {sending && (
        <div className="mb-3">
          <div className="d-flex justify-content-between small mb-1">
            <span>{current} / {total}</span>
            <span>{progress?.label || ''}</span>
          </div>
          <div className="progress">
            <div
              className="progress-bar"
              role="progressbar"
              style={{ width: `${total ? Math.round((current / total) * 100) : 0}%` }}
            />
          </div>
        </div>
      )}
      <div className="iti-maint-confirm-list">
        {groups.map((group) => {
          const names = group.sites.map((site) => site.name).join(', ');
          const ok = Boolean(group.email);
          return (
            <div key={group.key} className={`iti-maint-confirm-row${ok ? '' : ' is-skipped'}`}>
              <div>
                <div className="fw-semibold">{names}</div>
                <div className="small text-muted">{group.client || 'Client non lié'}</div>
              </div>
              <div className="small text-end">
                {ok ? group.email : 'Sans e-mail'}
                {group.sites.some((site) => overrides[site.id]) ? ' · saisi' : ''}
              </div>
            </div>
          );
        })}
      </div>
    </AppModal>
  );
}

export function ItiMaintenanceHistoryModal({ envoi, onClose, onResend, resending }) {
  const recipients = envoi?.recipients || [];
  const errorCount = envoi?.error_count || 0;

  return (
    <AppModal
      show={Boolean(envoi)}
      title={envoi?.subject}
      onClose={onClose}
      size="lg"
      centered
      scrollable
      className="iti-maint-modal"
      footer={(
        <>
          <AppButton variant="secondary" onClick={onClose}>Fermer</AppButton>
          {errorCount > 0 && (
            <AppButton onClick={onResend} loading={resending} loadingLabel="Renvoi…">
              Renvoyer les erreurs
            </AppButton>
          )}
        </>
      )}
    >
      {envoi ? (
        <>
          <div className="small text-muted mb-3">
            {envoi.sent_at ? new Date(envoi.sent_at).toLocaleString('fr-FR') : ''}
            {envoi.sent_by ? ` · ${envoi.sent_by}` : ''}
            {envoi.template_name ? ` · modèle ${envoi.template_name}` : ''}
            {envoi.test_mode ? ' · TEST' : ''}
            {envoi.reply_to ? ` · Reply-To ${envoi.reply_to}` : ''}
          </div>
          <div className="mb-3">
            {envoi.sent_count} envoyé(s)
            {envoi.skipped_count ? ` · ${envoi.skipped_count} ignoré(s)` : ''}
            {errorCount ? ` · ${errorCount} erreur(s)` : ''}
          </div>
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Destinataire</th>
                <th>E-mail</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {recipients.map((recipient, index) => (
                <tr key={`${envoi.id}-${recipient.hebergement_id || index}`}>
                  <td>
                    <div>{recipient.name}</div>
                    <div className="small text-muted">{recipient.client || ''}</div>
                  </td>
                  <td className="small">{recipient.to || recipient.email || '—'}</td>
                  <td>
                    {recipientStatusLabel(recipient.status)}
                    {recipient.error ? <div className="small text-danger">{recipient.error}</div> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}
    </AppModal>
  );
}

export function ItiMaintenanceEmailEditor({
  site,
  overrides,
  editingId,
  editingValue,
  onStart,
  onChange,
  onCancel,
  onSave,
}) {
  if (editingId === site.id) {
    return (
      <div className="d-flex gap-1">
        <AppInput
          size="sm"
          type="email"
          value={editingValue}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSave(site);
            if (e.key === 'Escape') onCancel();
          }}
          autoFocus
        />
        <AppButton size="sm" icon="mdi:check" title="Enregistrer" onClick={() => onSave(site)} />
      </div>
    );
  }

  const email = overrides[site.id] || site.email;
  return (
    <AppButton
      variant="link"
      size="sm"
      className="p-0 text-start"
      onClick={() => onStart(site)}
    >
      {email ? (
        <span>{email}{overrides[site.id] ? ' · saisi' : ''}</span>
      ) : (
        <AppBadge variant="warning">Sans e-mail</AppBadge>
      )}
    </AppButton>
  );
}
